import React from 'react';
import { BannerZoneLocation } from '@gin-betting/constants';
import core from '@gin-betting/core';

import { BettingWidgets } from 'app-constants';
import { Helper, Portal, Widget } from 'components';
import { withErrorBoundary } from 'hoc';
import {
    AsyncBetslipListSubscription,
    AsyncRestoreBetslipFromURL,
    AsyncRestrictions,
    AsyncSynchronizeBetslipState,
} from 'modules/betslip/async';
import { AsyncRequestFreebet } from 'modules/freebet/async';
import { MarketsTabConfigsCtx } from 'modules/match/async';
import matchList from 'modules/match-list';
import { AsyncOutrightsBanners } from 'modules/outright/async';
import promo from 'modules/promo';
import ui from 'modules/ui';
import GoogleAnalytics from 'services/analytics';
import {
    BetslipSidebarWrapper,
    HeaderMobile,
    LeftSidebar,
    Navigation,
    OddsStatisticFetcher,
} from 'views';
import { Header as CMSHeader } from '../cms-mock/async';
import useApp from './useApp';

import './app.scss';

const {
    hooks: { useSubscription },
    async: {
        constants: { SubscribeOrigin },
    },
} = core;

const {
    components: { BannersCarousel, TopMatchList },
} = promo;

const {
    components: { SettingsMenu },
} = ui;

const {
    components: { MatchListNavigation },
} = matchList;

const UserBetSubscription = () => {
    useSubscription({
        variables: {},
        config: {
            batch: false,
            origin: SubscribeOrigin.AppOrigin,
            method: 'onUpdateBet',
        },
    });

    return null;
};

const App: React.FC = ({ children }) => {
    const {
        areSportsAvailable,
        isAuthorized,
        isNewsPage,
        isBannersCarouselVisible,
        isOutrightsPage,
        sportType,
        shouldRenderTopMatches,
    } = useApp();

    if (isNewsPage)
        return (
            <div styleName="page-wrapper" className="__app-page-wrapper">
                <div styleName="middle">{children}</div>
            </div>
        );

    return (
        <React.Fragment>
            <CMSHeader />
            {isAuthorized && <UserBetSubscription />}
            <GoogleAnalytics />
            <AsyncBetslipListSubscription />
            <AsyncRestoreBetslipFromURL />
            {isAuthorized && <AsyncSynchronizeBetslipState />}
            <AsyncRequestFreebet />

            <Widget
                widget={BettingWidgets.SETTINGS}
                replacePortalContent={true}
                isFilled={true}
            >
                <SettingsMenu />
            </Widget>
            <Portal isPageDisabled={false}>
                <Helper />
            </Portal>

            <HeaderMobile />

            <div styleName="page-wrapper" className="__app-page-wrapper">
                <OddsStatisticFetcher />
                <Widget widget={BettingWidgets.CATEGORIZER}>
                    <LeftSidebar />
                </Widget>

                <div styleName="middle">
                    <Navigation />

                    {isOutrightsPage && (
                        <AsyncOutrightsBanners
                            zoneLocation={
                                BannerZoneLocation.TOP_OUTRIGHTS_SLIDER
                            }
                        />
                    )}

                    {isBannersCarouselVisible && areSportsAvailable && (
                        <BannersCarousel
                            zoneLocation={BannerZoneLocation.TOP_SLIDER}
                        />
                    )}

                    {shouldRenderTopMatches && (
                        <TopMatchList sportType={sportType} />
                    )}

                    <MatchListNavigation>
                        <MarketsTabConfigsCtx>{children}</MarketsTabConfigsCtx>
                    </MatchListNavigation>
                </div>
                <AsyncRestrictions>
                    <BetslipSidebarWrapper />
                </AsyncRestrictions>
            </div>
        </React.Fragment>
    );
};

export default withErrorBoundary(App);
